import * as Sentry from '@sentry/react';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { durableStorage } from '@/lib/durableStorage';
import { deviceLabel } from './deviceName';
import { browserNetwork } from './web';
import { nativeSecureStorage } from './secureStorage';
import type { Platform, Unsubscribe } from './types';

// Loaded on demand so the website never downloads the native plugin. Resolves
// with the module, never the plugin itself: a promise resolved with a Capacitor
// plugin proxy calls its `then`, which the native side rejects as unimplemented.
let appModule: Promise<typeof import('@capacitor/app')> | null = null;
const loadAppModule = () =>
  (appModule ??= import('@capacitor/app').catch((error: unknown) => {
    // A failed chunk load is retried on the next subscription.
    appModule = null;
    throw error;
  }));

/**
 * Capacitor registers listeners asynchronously: a caller that unsubscribes
 * before the registration resolves must still end up with no listener.
 */
function subscribeWhenReady(register: () => Promise<PluginListenerHandle>): Unsubscribe {
  let handle: PluginListenerHandle | null = null;
  let cancelled = false;
  register()
    .then((registered) => {
      if (cancelled) void registered.remove();
      else handle = registered;
    })
    .catch((error: unknown) => Sentry.captureException(error));
  return () => {
    cancelled = true;
    if (handle) void handle.remove();
    handle = null;
  };
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

// Message of the Android share plugin when the sheet is closed without a choice.
const SHARE_CANCELED = 'Share canceled';

/**
 * The cold-start URL is read once per app launch: remounting the listener
 * (StrictMode, sign-in) must not replay the link the app was opened with.
 */
let launchUrlConsumed = false;

export function __resetNativePlatformState(): void {
  launchUrlConsumed = false;
}

export const nativePlatform: Platform = {
  kind: 'native',
  // Same store as the web until a native adapter is worth it (VOL-14 lots 2-3).
  storage: durableStorage,
  // Cookies are unreliable in a webview (VOL-14 lot 5).
  authTransport: 'bearer',
  secureStorage: nativeSecureStorage,
  // The webview raises the browser's online/offline events.
  network: browserNetwork,
  deepLinks: {
    subscribe: (listener) => {
      let active = true;
      const unsubscribe = subscribeWhenReady(async () => {
        const { App } = await loadAppModule();
        const handle = await App.addListener('appUrlOpen', ({ url }) => {
          if (active) listener(url);
        });
        const launch = await App.getLaunchUrl();
        // Checked after the await: a listener dropped meanwhile leaves the link to the next one.
        if (active && !launchUrlConsumed && launch?.url) {
          launchUrlConsumed = true;
          listener(launch.url);
        }
        return handle;
      });
      return () => {
        active = false;
        unsubscribe();
      };
    },
  },
  lifecycle: {
    onResume: (listener) =>
      subscribeWhenReady(async () => {
        const { App } = await loadAppModule();
        return App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) listener();
        });
      }),
    onBackButton: (listener) =>
      subscribeWhenReady(async () => {
        const { App } = await loadAppModule();
        return App.addListener('backButton', () => listener());
      }),
    exit: async () => {
      const { App } = await loadAppModule();
      await App.exitApp();
    },
  },
  app: {
    info: async () => {
      const { App } = await loadAppModule();
      const { version } = await App.getInfo();
      return { version, os: Capacitor.getPlatform() === 'ios' ? 'ios' : 'android' };
    },
  },
  device: {
    keepAwake: async () => {
      const { KeepAwake } = await import('@capacitor-community/keep-awake');
      await KeepAwake.keepAwake();
    },
    allowSleep: async () => {
      const { KeepAwake } = await import('@capacitor-community/keep-awake');
      await KeepAwake.allowSleep();
    },
    tapFeedback: async () => {
      const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
      await Haptics.impact({ style: ImpactStyle.Light });
    },
    name: async () => {
      try {
        const { Device } = await import('@capacitor/device');
        return deviceLabel(await Device.getInfo());
      } catch {
        // Only a label: an older APK without the plugin still signs in, unnamed.
        return null;
      }
    },
  },
  browser: {
    // In-app browser tab (Custom Tabs / SFSafariViewController): the webview itself
    // must never navigate away from the app's bundle.
    open: async (url) => {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
    },
  },
  files: {
    // The webview ignores blob downloads: the file goes through the app cache to the
    // share sheet (WhatsApp, Files, Drive, email), which the FileProvider of the manifest serves.
    save: async (blob, filename) => {
      const [{ Filesystem, Directory }, { Share }] = await Promise.all([
        import('@capacitor/filesystem'),
        import('@capacitor/share'),
      ]);
      const { uri } = await Filesystem.writeFile({
        path: `exports/${filename.replace(/[\\/:*?"<>|]/g, '_')}`,
        data: await blobToBase64(blob),
        directory: Directory.Cache,
        recursive: true,
      });
      try {
        await Share.share({ files: [uri] });
        return 'saved';
      } catch (error) {
        if (error instanceof Error && error.message === SHARE_CANCELED) return 'dismissed';
        throw error;
      }
    },
  },
};
